import { action, computed, observable } from 'mobx';
import { IJsonSchema } from '@estee/elc-form';
import { IOptionItem } from '@estee/elc-dropdown';
import { IFieldDependencies, IState, IDropdownOption } from '../../domain/entities/Config/Config';
import Address, { AddressId } from '../../domain/entities/Address';
import ToggleBillingSameAsShippingUseCase from '../../use-cases/ToggleBillingSameAsShippingUseCase';
import ToggleIsCompanyBillingUseCase from '../../use-cases/ToggleIsCompanyBillingUseCase';
import SelectBillingAddressUseCase from '../../use-cases/SelectBillingAddressUseCase';
import { ICountry } from '../../api/IApiResponses';
import { getPhoneRegions } from '../../utils/AddressFormUtils';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { ADDRESS } from '../../constants/Errors';
import AddressViewController from '../AddressViewController';
import { BILLING_FORM_ERROR_SELECTED } from '../../use-cases/ValidateAndSubmitAddressesUseCase';
import { NOTIFICATION_GROUP } from '../../constants/Notifications';

interface IBillingViewControllerData {
  addresses: Address[];
  cities: IDropdownOption[];
  towns: string[];
  colonies: string[];
  districts: string[];
  streets: IDropdownOption[];
  wards: IDropdownOption[];
  billingCountries: ICountry[];
  countryId: number;
  fieldDependencies: IFieldDependencies;
  formStore: AddressFormStore;
  isUserRegistered: boolean;
  isNewAddressSelected: boolean;
  hasSuggestions: boolean;
  multipleCityState: IOptionItem[];
  regions: string[] | null;
  selectedId: string;
  statesList: IState[];
  validationSchema: IJsonSchema;
  isLoading: boolean;
  isLoaded: boolean;
  supportsTypeaheadInput: boolean;
  shouldShowAddressSection: boolean;
  shouldShowCancelSaveBtn: boolean;
  shouldShowAddressDropdown: boolean;
}

export default class BillingViewController extends AddressViewController {
  @observable billingCountries: ICountry[];

  @observable regions: string[] | null;

  @observable previousId: string;

  @observable isUserRegistered: boolean;

  @observable notificationsCollection: NotificationsCollection;

  @observable isLoaded = false;

  @action
  async loaded(): Promise<void> {
    await super.loaded();
    this.setRegions(getPhoneRegions(this.config.regionId, true));
    this.setBillingCountries(this.config.billingCountries);
    if (!this.shouldEnableNewSchema) {
    // TODO remove this when old schema is obsolete (RCO-4685 completed), the schema
    // will be loaded on demand at the base AddressViewController based on address type
      this.formStore.setAddressSchema({
        properties: this.config.billingAddressInputSchema,
        required: this.config.billingAddressInputSchemaRequired,
      }, {
        hasStates: !!this.statesList?.length,
      });
    }

    const { transaction } = this.transactionRepository;
    // if a new address form is about to be rendered, we don't want to reset data on component load if already touched
    if (transaction.billingAddress?.isNewAddress && !this.addressRepository.formStore.isTouched) {
      await new SelectBillingAddressUseCase(Address.NEW_ADDRESS_ID).execute();
    }

    this.setIsUserRegistered(this.transactionRepository.transaction?.user?.isUserRegistered);
    this.setPreviousId(this.sortedAddresses[0]?.id);
    await this.loadAddressOptions({ countryId: this.selectedCountryId });

    this.isLoaded = true;
  }

  @action
  setRegions = (regions: string[] | null): void => {
    this.regions = regions;
  };

  @action
  setBillingCountries = (countries: ICountry[]): void => {
    this.billingCountries = countries;
  };

  @action
  setIsUserRegistered = (registered: boolean): void => {
    this.isUserRegistered = registered;
  };

  @computed
  public get data(): IBillingViewControllerData {
    return {
      addresses: this.sortedAddresses,
      billingCountries: this.billingCountries,
      cities: this.cities,
      colonies: this.colonies,
      countryId: this.countryId,
      districts: this.districts,
      fieldDependencies: this.fieldDependencies,
      formStore: this.formStore,
      hasSuggestions: this.hasSuggestions,
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      isNewAddressSelected: this.isNewAddressSelected,
      isUserRegistered: this.isUserRegistered,
      multipleCityState: this.multipleCityState,
      regions: this.regions,
      selectedId: this.selectedId,
      shouldShowAddressDropdown: this.shouldShowAddressDropdown,
      shouldShowAddressSection: this.shouldShowAddressSection,
      shouldShowCancelSaveBtn: this.shouldShowCancelSaveBtn,
      statesList: this.statesList,
      streets: this.streets,
      supportsTypeaheadInput: this.config.supportsTypeaheadInput,
      towns: this.towns,
      validationSchema: this.validationSchema,
      wards: this.wards,
    };
  }

  @action
  setPreviousId = (value: string): void => {
    this.previousId = value;
  };

  @action
  public toggleBillingSameAsShipping = async (isSameAsShipping: boolean): Promise<void> => {
    const useCase = new ToggleBillingSameAsShippingUseCase(isSameAsShipping);
    await useCase.execute();
  };

  @action
  public toggleIsCompanyBilling = (isCompanyBilling: boolean): void => {
    const useCase = new ToggleIsCompanyBillingUseCase(isCompanyBilling);
    useCase.execute();
  };

  @action
  public updateSelectedId = (id: AddressId): void => {
    const isNewAddressSelected = Address.isNewAddress(id);
    const { transaction } = this.transactionRepository;

    // save previous address id if valid only
    if (!isNewAddressSelected) {
      this.setPreviousId(id);
    }
    // don't do anything if billing form is already set to new
    if (isNewAddressSelected && transaction?.billingAddress?.isNewAddress) {
      return;
    }

    const useCase = new SelectBillingAddressUseCase(id);
    useCase.execute();
  };

  @action
  public onCancelNewAddress = (): void => {
    this.updateSelectedId(this.previousId);
    this.onCancelClearSelectOptions();
  };

  @computed
  private get selectedId(): string {
    return this.transactionRepository.transaction?.billingAddress.id;
  }

  @computed
  private get isNewAddressSelected(): boolean {
    return this.transactionRepository.transaction?.billingAddress.isNewAddress;
  }

  @computed
  public get hasInvalidAddressError(): boolean {
    return this.notificationsCollection.hasNotification(ADDRESS.INVALID_ADDRESS, NOTIFICATION_GROUP.BILLING);
  }

  @computed
  public get hasInvalidAddressSavedError(): boolean {
    return this.notificationsCollection.hasNotification(
      BILLING_FORM_ERROR_SELECTED, NOTIFICATION_GROUP.BILLING,
    );
  }

  // For highlighting fields with error.
  @computed
  public get errorFields(): string[] {
    return this.errorFieldsToHighlight(NOTIFICATION_GROUP.BILLING);
  }

  @computed
  private get shouldShowAddressSection(): boolean {
    return (
      this.configRepository?.config?.useNewAddressDesign
        && !this.isNewAddressSelected
        && this.transactionRepository.transaction.user.isUserRegistered
    );
  }

  @computed
  private get shouldShowCancelSaveBtn(): boolean {
    return (
      this.configRepository?.config?.useNewAddressDesign
        && this.isNewAddressSelected
        && this.transactionRepository.transaction.user.isUserRegistered
    );
  }
}
